define('core/strings',[
    'loglevel',

    'translations',
], function (
    logger,

    translations
) {
    'use strict';

    var exports = {
        translations: translations,
    };

    /**
     * Gets a string's translation, if available.
     * `gettext` is preferred within jsx.
     *
     * This function can't handle variables on its own. It needs to be used with
     * `interpolate` to get the variables' values.
     * Ex. interpolate(get(string), context)
     *
     *
     * This function has to be renamed and called as `gettext` because we use jsxgettext
     * to parse for strings that need translations.
     * Ex. var gettext = strings.get
     * See how jsxgettext works: https://www.npmjs.com/package/jsxgettext
     *
     * Any strings in `gettext` calls will be sent to Transifex for translation.
     * This function should only be used after code review is complete to avoid
     * unnecessary translations.
     *
     * To see what strings will be translated run `grunt xgettext app=embed`.
     * It generates a file with all strings that would be translated from
     * your last embed build.
     *
     *
     * Only passing a string as the argument will return the translated string.
     * When testing there will be no translations, so it will simply return the
     * string that was passed.
     * Ex. get=('hello') -> 'hello'
     *
     * Variables go inside %()s. However, this function doesn't handle replacing the variable
     * Ex. get('hey %(you)s') -> 'hey %(you)s'
     *
     * @param  {string} text - String to be translated
     * @returns {string} - The translated string, if available, else the original string
     */
    exports.get = function get(text) {
        var trans = exports.translations[text];
        return trans === undefined ? text : trans;
    };

    /**
     * Replaces the variable in a string with its value in the given context.
     * This variable will not be translated.
     *
     * Should be used with a `get` call.
     * Ex. interpolate(get(string), ctx)
     *
     *
     * Variables must be within %()s inside of the passed string.
     * Ex. interpolate(get('hey %(you)s'), { you : 'Jack' }) -> 'hey Jack'
     *
     * It can also take multiple variables.
     * Ex. interpolate(get('%(hey)s %(you)s'), { hey: 'hi', you: 'Jack' }) -> 'hi Jack'
     *
     * @param  {string} string - String with variables to replace
     * @param  {Object} ctx - Context for the variables
     * @returns {string} - String with variables replaced by their values
     */
    exports.interpolate = function interpolate(string, ctx) {
        function fromContext(key) {
            var result = '';
            if (key in ctx)
                result = ctx[key] !== undefined && ctx[key] !== null ? ctx[key].toString() : '';
            else
                logger.error('Key `' + key + '` not found in context for: ', string);

            return result;
        }

        return string.replace(/%\(\w+\)s/g, function (match) {
            return fromContext(match.slice(2, -2));
        });
    };

    /**
     * Gets a string's translation, if available.
     * This is the preferred function within jsx. It's easier to use than get
     * and interpolate, but returns an array so it's clunky to use outside jsx.
     *
     *
     * This function has to be called as `gettext` because we use jsxgettext
     * to parse for strings that need translations.
     * See how jsxgettext works: https://www.npmjs.com/package/jsxgettext
     *
     * Any strings in `gettext` calls will be sent to Transifex for translation.
     * This function should only be used after code review is complete to avoid
     * unnecessary translations.
     *
     * To see what strings will be translated run `grunt xgettext app=embed`.
     * It generates a file with all strings that would be translated from
     * your last embed build.
     *
     *
     * Only passing a string as the argument will return the translated string.
     * When testing there will be no translations, so it will simply return the
     * string that was passed.
     * Ex. gettext('hello') -> ['hello']
     *
     * You can use variables in strings by using %()s and passing its value in
     * the context object. The variable is not translated.
     * Ex. gettext('hey %(you)s', { you : 'Jack' }) -> ['hey ', 'Jack']
     *
     * You can use multiple variables.
     * Ex. gettext('%(hey)s %(you)s', { hey: 'hi', you: 'Jack' }) -> ['hi', ' ',  'Jack']
     *
     * This also means that you can't use %()s without a variable.
     * Ex. gettext('hey %(you)s') -> error
     *
     * @param  {string} string - String to be translated
     * @param  {Object} [ctx] - Context for variables within the string
     * @returns {Array} - Array containing the translated string and the variables' values
     */
    exports.gettext = function gettext(string, ctx) {
        string = exports.get(string);
        ctx = ctx || {};
        return string.split(/(%\(\w+\)s)/g).map(function (part) {
            var match = part.match(/%\((\w+)\)s/);
            if (match) {
                if (match[1] in ctx)
                    part = ctx[match[1]];
                else
                    logger.error('Key `' + match[1] + '` not found in context for: ' + string);
            }
            if (part === '')
                return null;
            return part;
        });
    };

    return exports;
});

